// frontend/src/navigation/CommandPalette.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Dialog, InputBase, List, ListItemButton, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { SP, FS, PANEL_RADIUS, hoverOnly } from '../theme/tokens'
import EmptyState from '../components/common/EmptyState'
import { alleEintraege, suche } from './paletteEntries'
import { holeZuletzt, merkeBesuch, mitZuletzt } from './recentVisits'

/**
 * CommandPalette — Cmd+K / Ctrl+K (W-20).
 *
 * @description
 *   Von 73 Routen haben 49 keinen Sidebar-Eintrag. Die Leiste bleibt eine
 *   kuratierte Auswahl; die Palette macht den Rest erreichbar. Damit wird jede
 *   andere Navigationsentscheidung sicher: eine Flaeche darf umziehen,
 *   verschmelzen oder verschwinden, ohne gestrandet zu sein.
 *
 * 🔴 ZUM FOKUS-WAECHTER VON ATLASSIAN. Der lautet „die Palette oeffnet nicht,
 *   wenn es aussieht, als wolltest du gerade tippen". Der Plan warnt selbst,
 *   dass er sich kaputt anfuehlt, wo Leute in ein Filterfeld tippen und dann
 *   Cmd+K greifen. Hier gibt es ihn deshalb NICHT: die Palette oeffnet nur auf
 *   die ausdrueckliche Tastenkombination, und Cmd+K ist keine Taste, die man
 *   beim Tippen trifft. Es gibt nichts zu schuetzen, also wird nichts
 *   geschuetzt — ein Waechter ohne Gegner ist nur eine Fehlerquelle.
 *
 * 🔴 WARUM HANDGEBAUT UND NICHT `Autocomplete`. MUI bringt eines mit, aber
 *   seine Tastatursemantik ist auf Formularfelder ausgelegt (Auswahl uebernimmt
 *   einen Wert). Hier uebernimmt Enter eine NAVIGATION und schliesst. Das sind
 *   zwei verschiedene Vertraege; sie ueber Props zu verbiegen kostet mehr, als
 *   die vier Tasten selbst zu behandeln.
 *
 * Tasten: ↑ ↓ bewegen · Enter waehlt · Esc schliesst. Beim Oeffnen steht die
 * Auswahl auf dem ersten Treffer, damit Enter ohne Umweg funktioniert.
 */
export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [aktiv, setAktiv] = useState(0)
  const listeRef = useRef(null)

  const eintraege = useMemo(() => alleEintraege(), [])

  // 🔴 Der Speicher ist ZUSTAND, nicht ein Aufruf im Render. `holeZuletzt()`
  // ist ein lesender Seiteneffekt; in einem `useMemo` gelesen, muesste man
  // `open` als Auffrisch-Trigger nachschieben — und das ist genau der
  // Schummel, den der Linter zu Recht bemaengelt. Beim Oeffnen gelesen, beim
  // Navigieren geschrieben: dann stimmt die Reihenfolge von selbst.
  const [zuletzt, setZuletzt] = useState([])
  useEffect(() => {
    if (open) { setQ(''); setAktiv(0); setZuletzt(holeZuletzt()) }
  }, [open])

  // Zuletzt-Besuchtes ordnet NUR bei leerer Eingabe. Sobald getippt wird,
  // entscheidet die Suche — sonst schluege ein alter Besuch einen besseren
  // Treffer. Das ist Atlassians „Recent"-Mechanismus, nicht mehr.
  const treffer = useMemo(() => {
    const basis = suche(eintraege, q)
    return q.trim() ? basis : mitZuletzt(basis, zuletzt)
  }, [eintraege, q, zuletzt])

  useEffect(() => { setAktiv(0) }, [q])

  const waehle = (eintrag) => {
    if (!eintrag) return
    merkeBesuch(eintrag.path)
    setZuletzt(holeZuletzt())
    onClose()
    navigate(eintrag.path)
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setAktiv(i => Math.min(i + 1, treffer.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setAktiv(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      waehle(treffer[aktiv])
    }
  }

  const optionId = (i) => `palette-option-${i}`

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      // Oben ausgerichtet statt zentriert: die Palette ist ein Sprungbrett,
      // kein Dialog, den man liest.
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      // 🔴 Der zugaengliche Name gehoert auf das PAPER, nicht auf `<Dialog>`.
      // `role="dialog"` traegt das Paper; ein `aria-label` am `<Dialog>` landet
      // auf der Modal-Wurzel darueber, die keine Rolle hat. Ein Screenreader
      // kuendigt den Dialog dann OHNE Namen an — und `getByRole('dialog',
      // { name: … })` findet ihn nicht. Gemessen am 2026-09-30 im Browser:
      // Paper `aria-label=null`, die Modal-Wurzel trug den Text.
      PaperProps={{
        'aria-label': 'Command palette',
        sx: { mt: '12vh', borderRadius: `${PANEL_RADIUS}px`, overflow: 'hidden' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: SP.sm, px: SP.md, py: SP.sm, borderBottom: 1, borderColor: 'divider' }}>
        <SearchIcon sx={{ color: 'text.disabled', flexShrink: 0 }} aria-hidden />
        <InputBase
          autoFocus
          fullWidth
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Jump to a page…"
          inputProps={{
            'aria-label': 'Jump to a page',
            'aria-controls': 'palette-listbox',
            'aria-activedescendant': treffer[aktiv] ? optionId(aktiv) : undefined,
            role: 'combobox',
            'aria-expanded': true,
            'aria-autocomplete': 'list',
          }}
        />
        <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0, fontSize: FS.xs }}>
          Esc
        </Typography>
      </Box>

      <List
        id="palette-listbox"
        ref={listeRef}
        role="listbox"
        aria-label="Pages"
        sx={{ maxHeight: '50vh', overflowY: 'auto', py: 0.5 }}
      >
        {!q.trim() && zuletzt.length > 0 && treffer.length > 0 && (
          <Typography
            variant="caption"
            sx={{ display: 'block', px: SP.md, pt: SP.sm, pb: 0.5, color: 'text.disabled', fontSize: FS.xs, letterSpacing: '.04em', textTransform: 'uppercase' }}
          >
            Recent
          </Typography>
        )}
        {treffer.length === 0 && (
          // `suche()` filtert die Eintraege — leer heisst hier Nichttreffer,
          // nicht leere Palette.
          <EmptyState reason="no-match" message="No page has that name." />
        )}
        {treffer.map((e, i) => (
          <ListItemButton
            key={e.path}
            id={optionId(i)}
            role="option"
            aria-selected={i === aktiv}
            selected={i === aktiv}
            onClick={() => waehle(e)}
            onMouseEnter={() => setAktiv(i)}
            sx={{ gap: SP.sm, py: 1, ...hoverOnly({ bgcolor: 'action.hover' }) }}
          >
            <Box sx={{ display: 'flex', color: 'text.secondary', flexShrink: 0 }} aria-hidden>
              {e.icon}
            </Box>
            <Typography sx={{ fontSize: FS.base, flex: 1, minWidth: 0 }} noWrap>
              {e.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', fontSize: FS.sm, flexShrink: 0, fontFamily: 'monospace' }}
            >
              {e.path}
            </Typography>
          </ListItemButton>
        ))}
      </List>
    </Dialog>
  )
}

/** Der Tastengriff. Eigene Funktion, damit Tests ihn ohne DOM pruefen koennen. */
export function istPaletteGeste(e) {
  // 🔴 `Boolean(...)` ist nicht Kosmetik: ohne die Umhuellung gibt die
  // Funktion bei fehlenden Feldern `undefined` zurueck statt `false`. Als
  // Waechter wirkt das gleich, als PREDIKAT ist es falsch — und wer sie
  // spaeter mit `=== false` prueft, faellt darauf herein.
  return Boolean((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K'))
}
